import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { formatPhoneNumber } from 'react-phone-number-input';

import { Typography } from '@mui/material';

import { useRouter } from 'next/router';

import { Constants } from '@arrived/common';
import { PrivacyTermsWrapper } from '@arrived/components';

import { NavLinkRaw } from '../components/navigation/NavLink';

import { PublicFooter } from '../components/footer';

const titleVariant = 'h3';
const subtitleVariant = 'h4';
const paragraphVariant = 'body2';

export const DisclaimerPage = () => {
  const router = useRouter();

  const currentPath = `${Constants.mainUrl}${router.asPath}`;

  const supportEmailLink = (
    <NavLinkRaw href={`mailto:${Constants.arrivedSupportEmail}`}>
      <FormattedMessage id="arrived-support-email-address" defaultMessage={Constants.arrivedSupportEmail} />
    </NavLinkRaw>
  );

  return (
    <>
      <PrivacyTermsWrapper className="disclaimer-page">
        <Typography variant={titleVariant} marginTop={6}>
          <FormattedMessage id="disclaimer-title-1" defaultMessage="Communication Disclaimer" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-1"
            defaultMessage='This disclaimer is deemed to be incorporated by reference in its entirety into any social media communication, advertisement, email or other communication or disclosure which contains the following link: {link} (each, a "Communication").'
            values={{
              link: <NavLinkRaw href={currentPath}>{currentPath}</NavLinkRaw>,
            }}
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-2"
            defaultMessage='Arrived Homes, LLC may be referred to as an "Arrived Entity".'
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-3"
            defaultMessage="The offer and sale of securities is being facilitated by an unaffiliated third-party registered broker-dealer (member FINRA/SIPC) only in U.S. states where such broker-dealer is registered. Neither Arrived Holdings, Inc., Arrived Homes, LLC nor any third-party broker-dealer provides any investment advice or make any investment recommendations to any persons, ever, and no communication through the arrived.com website or in any other medium should be construed as such. The Communication and the subject matter contained within it, does not constitute a solicitation to purchase or an offer to sell any securities."
          />
        </Typography>

        <Typography variant={titleVariant} mt={3}>
          <FormattedMessage id="communications-disclaimer.content-disclaimer.title" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage id="communications-disclaimer.content-disclaimer.part-1" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage id="communications-disclaimer.content-disclaimer.part-2" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage id="communications-disclaimer.content-disclaimer.part-3" />
        </Typography>

        <Typography variant={titleVariant} marginTop={3}>
          <FormattedMessage id="disclaimer-title-2" defaultMessage="Offerings in Arrived Entities" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-4"
            defaultMessage='Investors are able to acquire membership interests in only a series of an Arrived Entity (a "<u>Series</u>"), a Delaware series limited liability company (the "<u>Company</u>"), via the Arrived&trade; platform (the "<u>Arrived&trade; Platform</u>" or "<u>Platform</u>"). The use of the word "<u>Share</u>" or "<u>Stock</u>" in any Communication refers to membership interests (an "<u>Interest</u>" or "<u>Interests</u>") in a Series. Each offering of shares in each Series of the Company is defined herein as an "<u>Offering</u>" and is subject to its own offering circular (each an "<u>Offering Circular</u>") as the case may be. These important documents are available via the Arrived&trade; Platform under the "<u>Invest</u>" heading for each Offering, or by requesting a copy by e-mailing {supportEmailLink}, or publicly via the U.S. Securities & Exchange Commission EDGAR service, and should be read by all investors prior to acquiring any membership interests.'
            values={{ u: (parts: ReactNode[]) => <u>{parts}</u>, supportEmailLink }}
          />
        </Typography>
        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-5"
            defaultMessage='The Company is structured as a Delaware series limited liability company that issues different Series of Interests specific to one or more collectible underlying assets ("<u>Underlying Assets</u>"). Each Series of Interests is not a separate legal entity, but is intended to segregate assets, liabilities, profits and taxes pertaining to the Underlying Asset from each other Series of Interests (which may own other assets). Each Offering entitles a person to acquire an ownership Interest in a Series of the Company and not, for the avoidance of doubt, in (i) the Company, (ii) any other Series of the Company other than the Series of Interests subject to the Offering at that time, (iii) Arrived Holdings, Inc. (the "<u>Manager</u>" and the "<u>Asset Manager</u>"), (iv) the Arrived&trade; Platform or (v) any Underlying Asset held by each Series of the Company.'
            values={{ u: (parts: ReactNode[]) => <u>{parts}</u> }}
          />
        </Typography>
        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-6"
            defaultMessage='Each Offering is being conducted (i) under Tier II of Regulation A of the Securities Act of 1933, as amended (the "<u>Securities Act</u>") as amended, (ii) only through an Offering Circular and (iii) exclusively through a broker-dealer registered with the U.S. Securities and Exchange Commission (the "<u>SEC</u>") and a member of the Financial Industry Regulatory Authority, Inc. and the Securities Investor Protection Corporation and other necessary state or other regulators, and only in such states where the broker-dealer is registered. Membership Interests are being offered and sold to "qualified investors" under Tier II of Regulation A under the Securities Act, pursuant to an Offering Circular as qualified by the SEC. Even so, each investor must rely on its own examination of the Company, the Series, the Interests and the Underlying Asset and the terms of the Offering, including the risks and merits involved, before making any investment.'
            values={{ u: (parts: ReactNode[]) => <u>{parts}</u> }}
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-7"
            defaultMessage='From time to time the Company will conduct "testing the waters" campaigns to gauge market demand from potential investors for an Offering under Tier II of Regulation A of the Securities Act. No money or other consideration will be solicited, and if sent in response, it will not be accepted. No sales of securities will be made or commitment to purchase accepted until qualification of the Offering Circular by the SEC and approval of any other required government or regulatory agency. An indication of interest made by a prospective investor is non-binding and involves no obligation or commitment of any kind. Any offer to buy securities may be withdrawn or revoked, without obligation or commitment of any kind, at any time before notice of its acceptance given after the qualification date. No offer to buy securities can be accepted and no part of the purchase price can be received without an Offering Circular that has been qualified by the SEC, which we urge prospective investors to read carefully. A copy of the most recent version of the Offering Circular may be obtained by contacting the Company at: {arrivedAddress}, {arrivedPhoneNumber}, emailing {supportEmailLink}, or via the Arrived&trade; Platform under the "Invest" heading or accessed online here: {secEdgarServiceLink}.'
            values={{
              supportEmailLink,
              arrivedAddress: `${Constants.arrivedAddress.street}, ${Constants.arrivedAddress.city}`,
              arrivedPhoneNumber: (
                <NavLinkRaw href={`tel:${Constants.arrivedPhoneNumber}`}>
                  {formatPhoneNumber(`+${String(Constants.arrivedPhoneNumber)}`)}
                </NavLinkRaw>
              ),
              secEdgarServiceLink: (
                <NavLinkRaw href="https://www.sec.gov/edgar/browse/?CIK=1821720">
                  <FormattedMessage id="sec-edgar-service-link-text" defaultMessage="SEC EDGAR Service" />
                </NavLinkRaw>
              ),
            }}
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-8"
            defaultMessage='No Offering is being made in any jurisdiction where such an offer or solicitation is not lawful or is prohibited or where the broker-dealer, through whom each Offering is being conducted, is not registered. Each Offering of the membership Interests is made pursuant to an exemption from the registration requirements of the Securities Act and certain state securities laws. The Company is not required to file periodic reports (such as reports on Forms 10-K and 10-Q) with the SEC, so there is little publicly available information about its business, assets, liabilities, results of operations and other information that would typically be available regarding publicly traded securities. The Company is not registered as an investment company under the Investment Company Act of 1940, as amended (the "<u>Investment Company Act</u>") and the membership Interests do not have the benefit of the protections of the Investment Company Act. Furthermore, the Manager is not registered as an investment adviser under the Investment Advisers Act of 1940, as amended (the "<u>Investment Advisers Act</u>"), and the members of the Company will not have the benefit of the protections of the Investment Advisers Act.'
            values={{ u: (parts: ReactNode[]) => <u>{parts}</u> }}
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-9"
            defaultMessage="Neither the SEC nor any state securities commission has approved or disapproved the membership Interests nor have any of the foregoing passed upon or endorsed the merits of an Offering or the accuracy or adequacy of any of the Circular or any Communication. Any representation to the contrary is a criminal offense."
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-10"
            defaultMessage="There is no trading market for the membership Interests at this time and there can be no assurance that such a market will develop in the foreseeable future. The membership Interests may not be resold or otherwise disposed of by an investor unless there are available exemptions from registration under federal and applicable state securities laws (and other requirements are met, which may include an opinion of counsel), or such transfer is made in compliance with the registration requirements of such laws. Accordingly, investors must bear the risk of loss for an indefinite period of time."
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-11"
            defaultMessage='An investment in membership Interests may involve significant risks. Only investors who can bear the economic risk of the investment for an indefinite period of time and the loss of their entire investment should invest in the membership Interests. See "Risk Factors" below and in the Offering Circular.'
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-12"
            defaultMessage="No offeree will be accepted as a subscriber who does not make the representations set forth in the subscription agreement accompanying each Offering Circular, including, when applicable, the representation that such offeree is an accredited investor, and, when applicable, that the investment amount does not exceed 10% of the offeree’s net worth or annual income. Investors also will be required to represent that they are familiar with and understand the terms of each relevant Offering, among other things. Investors may also be required to provide additional information to verify their identity or investor status."
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-13"
            defaultMessage='The Interests will not be offered or sold to prospective investors subject to the Employee Retirement Income Security Act of 1974 and regulations thereunder, as amended ("<u>ERISA</u>").'
            values={{ u: (parts: ReactNode[]) => <u>{parts}</u> }}
          />
        </Typography>

        <Typography variant={titleVariant} marginTop={3}>
          <FormattedMessage id="disclaimer-title-3" defaultMessage="Notice to Foreign Investors" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-14"
            defaultMessage="The Communication is directed solely to persons located within the United States. If the recipient of the Communication lives outside the United States, it is their responsibility to fully observe the laws of any relevant territory or jurisdiction outside the United States in connection with any purchase of membership Interests, including obtaining required governmental or other consents or observing any other required legal or other formalities."
          />
        </Typography>

        <Typography variant={titleVariant} marginTop={3}>
          <FormattedMessage id="disclaimer-title-4" defaultMessage="Risk Factors" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-15"
            defaultMessage="Each Offering of membership Interests is highly speculative in nature, involves a high degree of risk and should be purchased only by persons who can afford to lose their entire investment. The investments in any Offering are not bank deposits (and thus not insured by the FDIC or by any other federal governmental agency), are not guaranteed by Arrived Holdings, Inc., Arrived Homes, LLC or any third-party broker-dealer and may lose value. The risks described below should not be considered as an exhaustive list of the risks that prospective investors should consider before investing in the membership Interests. All prospective investors should consult the relevant Offering Circular before purchasing membership Interests in a Series of the Company, which Offering Circular may describe the following risks in more detail and additional risks pertinent to the specific Offering."
          />
        </Typography>

        <Typography variant={subtitleVariant} marginTop={1}>
          <FormattedMessage
            id="disclaimer-subtitle-1"
            defaultMessage="Risks Relating to the Structure, Operation and Performance of the Company"
          />
        </Typography>

        <ul>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-16"
                defaultMessage="The current novel coronavirus, or COVID-19, pandemic or the future outbreak of any other highly infectious or contagious diseases, could materially and adversely impact or cause disruption to our performance, financial condition, results of operations, cash flows and ability to pay distributions. Further, the pandemic has caused disruptions in the U.S. and global economies and financial markets and created widespread business continuity issues of an as yet unknown magnitude and duration."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-17"
                defaultMessage="The COVID-19 pandemic has resulted in a general decline in real estate transactions and may adversely affect our growth prospects in the near term, and possibly for an extended period, depending upon the duration of the pandemic and its effects on the economy generally and the real estate market more particularly."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-18"
                defaultMessage="We are a newly formed entity with minimal prior operating history, which makes our future performance difficult to predict."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-19"
                defaultMessage="An investment in a series Offering constitutes only an investment in that series and not in our Company or directly in any property."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-20"
                defaultMessage="Each of our Company’s series will hold an Interest in a single property, a non-diversified investment."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-21"
                defaultMessage="We have no historical financial operations and only recently commenced operations."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-22"
                defaultMessage="There can be no guarantee that our Company will reach its funding target from potential investors with respect to any series or future proposed series."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-23"
                defaultMessage="We may not be able to control our operating costs or our expenses may remain constant or increase, even if our revenues do not increase, causing our results of operations to be adversely affected."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-24"
                defaultMessage="Competition could limit our ability to acquire attractive investment opportunities and increase the costs of those opportunities which may adversely affect us, including our profitability, and impede our growth."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-25"
                defaultMessage="Competition may impede our ability to attract or retain tenants or re-lease space, which could adversely affect our results of operations and cash flow."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-26"
                defaultMessage="Investments we make will be consistent with our intention for each series to qualify to be taxed as a REIT unless the Manager determines that not qualifying as a REIT is in the best interests of a Series"
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-27"
                defaultMessage="We may fail to successfully operate acquired properties, which could adversely affect us and impede our growth."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-28"
                defaultMessage="Disruptions in the financial markets or deteriorating economic conditions could adversely impact the residential real estate market, which could hinder our ability to implement our business strategy and generate returns to you."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-29"
                defaultMessage="You may be more likely to sustain a loss on your investment because the Manager does not have as strong an economic incentive to avoid losses as do Managers who have made significant equity investments in their companies."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-30"
                defaultMessage="Any adverse changes in the Manager’s financial health or our relationship with the Manager or its affiliates could hinder our operating performance and the return on your investment."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-31"
                defaultMessage="Compliance with governmental laws, regulations and covenants that are applicable to our residential properties may adversely affect our business and growth strategies."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-32"
                defaultMessage="If our Company’s series limited liability company structure is not respected, then investors may have to share any liabilities of our Company with all investors and not just those who hold the same series as them."
              />
            </Typography>
          </li>
        </ul>

        <Typography variant={subtitleVariant} marginTop={1}>
          <FormattedMessage id="disclaimer-subtitle-2" defaultMessage="Risks Relating to the Offerings" />
        </Typography>

        <ul>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-33"
                defaultMessage="We are offering our Interests pursuant to Tier 2 of Regulation A and we cannot be certain if the reduced disclosure requirements applicable to Tier 2 issuers will make our Interests less attractive to investors as compared to a traditional initial public offering."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-34"
                defaultMessage="We are subject to ongoing public reporting requirements that are less rigorous than rules for more mature public companies, and our investors receive less information."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-35"
                defaultMessage="There may be deficiencies with our internal controls that require improvements, and if we are unable to adequately evaluate internal controls, we may be subject to sanctions."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-36"
                defaultMessage="If we are required to register under the Exchange Act of 1934, as amended, or the Exchange Act, it would result in significant expense and reporting requirements that would place a burden on the Manager and may divert attention from management of the properties by the Manager or could cause the Manager to no longer be able to afford to run our business."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-37"
                defaultMessage="If our Company were to be required to register under the Investment Company Act or the Manager were to be required to register under the Investment Advisers Act, it could have a material and adverse impact on the results of operations and expenses of each series and the Manager may be forced to liquidate and wind up each series or rescind the Offerings for any of the Series."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-38"
                defaultMessage="Possible Changes in Federal Tax Laws may materially adversely affect the value of your investment in our Interests."
              />
            </Typography>
          </li>
        </ul>

        <Typography variant={subtitleVariant} marginTop={1}>
          <FormattedMessage id="disclaimer-subtitle-3" defaultMessage="Risks Related to Conflicts of Interest" />
        </Typography>

        <ul>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-39"
                defaultMessage="We are dependent on the Manager and its affiliates and their key personnel who provide services to us through the operating agreement, and we may not find a suitable replacement if the operating agreement is terminated, or if key personnel leave or otherwise become unavailable to us, which could have a material adverse effect on our performance."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-40"
                defaultMessage="The ability of the Manager and its officers and other personnel to engage in other business activities, including managing other similar companies, may reduce the time the Manager spends managing the business of our Company and may result in certain conflicts of interest."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-41"
                defaultMessage="The terms of the operating agreement make it so that it may adversely affect our inclination to end our relationship with the Manager."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-42"
                defaultMessage="The operating agreement contains provisions that reduce or eliminate duties (including fiduciary duties) of the Manager."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-43"
                defaultMessage="There are conflicts of interest among us, the Manager and its affiliates."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-44"
                defaultMessage="We do not have a policy that expressly prohibits our directors, officers, security holders or affiliates from having a direct or indirect pecuniary interest in any transaction to which we or any of our subsidiaries has an interest or engaging for their own account in business activities of the types conducted by us."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-45"
                defaultMessage="The Manager’s liability is limited under the operating agreement, and we have agreed to indemnify the Manager against certain liabilities.  As a result, we may experience poor performance or losses of which the Manager would not be liable."
              />
            </Typography>
          </li>
        </ul>

        <Typography variant={subtitleVariant} marginTop={1}>
          <FormattedMessage
            id="disclaimer-subtitle-4"
            defaultMessage="Risks Related to Real Estate Investments Generally"
          />
        </Typography>

        <ul>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-46"
                defaultMessage="Our real estate assets will be subject to the risks typically associated with real estate."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-47"
                defaultMessage="Many factors impact the residential rental market, and if rents do not increase sufficiently to keep pace with rising costs of operations, our income and distributable cash will decline."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-48"
                defaultMessage="We anticipate involvement in a variety of litigation."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-49"
                defaultMessage="We may be subject to unknown or contingent liabilities related to properties that we acquire for which we may have limited or no recourse against the sellers."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-50"
                defaultMessage="We may not be able to sell our properties at a price equal to, or greater than, the price for which we purchased such properties, which may lead to a decrease in the value of our assets."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-51"
                defaultMessage="We may be unable to renew leases or re-lease space as leases expire."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-52"
                defaultMessage="The actual rents we receive for a property may be less than estimated market rents, and we may experience a decline in realized rental rates from time to time, which could adversely affect our financial condition, results of operations and cash flow."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-53"
                defaultMessage="Properties that have significant vacancies could be difficult to sell, which could diminish the return on these properties."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-54"
                defaultMessage="Our dependence on rental revenue may adversely affect us, including our profitability, our ability to meet our debt obligations and our ability to make distributions to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-55"
                defaultMessage="We may engage in development, redevelopment or repositioning activities in the future, which could expose us to different risks that could adversely affect us, including our financial condition, cash flow and results of operations."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-56"
                defaultMessage="Our properties may be subject to impairment charges."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-57"
                defaultMessage="If a tenant declares bankruptcy, we may be unable to collect balances due under relevant leases, which could adversely affect our financial condition and ability to pay distributions to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-58"
                defaultMessage="Joint venture investments could be adversely affected by our lack of sole decision-making authority, our reliance on the financial condition of co-venturers and disputes between us and our co-venturers."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-59"
                defaultMessage="Property taxes could increase due to property tax rate changes or reassessment, which could impact our cash flow."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-60"
                defaultMessage="Uninsured losses relating to real property or excessively expensive premiums for insurance coverage, including due to the non-renewal of the Terrorism Risk Insurance Act of 2002, or the TRIA, could reduce our cash flows and the return on our investors’ investments."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-61"
                defaultMessage="Climate change may adversely affect our business."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-62"
                defaultMessage="Acquiring or attempting to acquire multiple properties in a single transaction may adversely affect our operations."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-63"
                defaultMessage="Tenant relief laws may negatively impact our rental income and profitability."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-64"
                defaultMessage="Rent control or rent stabilization laws could prevent us from raising rents to offset increases in operating costs."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-65"
                defaultMessage="Our targeted investments may include condominium interests. Condominium interests are subject to special risks that may reduce your return on investment."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-66"
                defaultMessage="Real estate investments are relatively illiquid and may limit our flexibility."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-67"
                defaultMessage="The failure of any bank in which we deposit our funds could reduce the amount of cash we have available to pay distributions to our investors and make additional investments."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-68"
                defaultMessage="The occurrence of a cyber incident, or a deficiency in our cyber security, could negatively impact our business by causing a disruption to our operations, a compromise or corruption of our confidential information, or damage to our business relationships, all of which could negatively impact our financial results."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-69"
                defaultMessage="We may enter into long-term leases with tenants in certain properties, which may not result in fair market rental rates over time."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-70"
                defaultMessage="We will depend on tenants for our revenue, and lease defaults or terminations could reduce our net income and limit our ability to make distributions to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-71"
                defaultMessage="Potential development and construction delays and resultant increased costs and risks may hinder our operating results and decrease our net income."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-72"
                defaultMessage="Actions of any joint venture partners that we may have in the future could reduce the returns on joint venture investments and decrease our investors’ overall return."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-73"
                defaultMessage="Costs imposed pursuant to governmental laws and regulations may reduce our net income and the cash available for distributions to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-74"
                defaultMessage="Costs associated with complying with the Americans with Disabilities Act and similar laws (including but not limited to Fair Housing Amendments Act of 1988 and the rehabilitation Act of 1973) may decrease cash available for distributions to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-75"
                defaultMessage="Declines in the market values of the assets we invest in may adversely affect periodic reported results of operations and credit availability, which may reduce earnings and, in turn, cash available for distribution to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-76"
                defaultMessage="A prolonged economic slowdown, a lengthy or severe recession or declining real estate values could harm our operations."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-77"
                defaultMessage="Deficiencies in our internal control over financial reporting could adversely affect our ability to present accurately our financial statements and could materially and adversely affect us, including our business, reputation, results of operations, financial condition or liquidity."
              />
            </Typography>
          </li>
        </ul>

        <Typography variant={subtitleVariant} marginTop={1}>
          <FormattedMessage id="disclaimer-subtitle-5" defaultMessage="U.S. Federal Income Tax Risks" />
        </Typography>

        <ul>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-78"
                defaultMessage="The failure of a Series to qualify or remain qualified as a REIT would subject the Series to U.S. federal income tax and potentially state and local tax and would adversely affect the Series’ operations and the market price of the Series’ Interests."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-79"
                defaultMessage="Even if a Series qualifies as a REIT, in certain circumstances, it may incur tax liabilities that would reduce its cash available for distribution to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-80"
                defaultMessage="The taxation of distributions to our investors can be complex; however, distributions that we make to our investors generally will be taxable as ordinary income or constitute a return of capital, which may reduce your anticipated return from an investment in us."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-81"
                defaultMessage="Dividends payable by REITs generally do not qualify for the reduced tax rates available for some dividends."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-82"
                defaultMessage={`If a Series were considered to actually or constructively pay a "preferential dividend" to certain of our investors, the Series' status as a REIT could be adversely affected.`}
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-83"
                defaultMessage="Complying with REIT requirements may limit our ability to hedge our liabilities effectively and may cause us to incur tax liabilities."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-84"
                defaultMessage="The ability of the Manager to revoke the REIT qualification of a Series without approval may subject a Series to U.S. federal income tax and reduce distributions to our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-85"
                defaultMessage="Legislative or regulatory action with respect to tax laws and regulations could adversely affect our Company and our investors."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-86"
                defaultMessage="The ownership restrictions of the Internal Revenue Code for REITs and the 9.8% ownership limit in the operating agreement may inhibit market activity in our Interests and restrict our business combination opportunities."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-87"
                defaultMessage="Potential characterization of distributions or gain on sale may be treated as unrelated business taxable income to tax-exempt investors."
              />
            </Typography>
          </li>
        </ul>

        <Typography variant={subtitleVariant} marginTop={1}>
          <FormattedMessage id="disclaimer-subtitle-6" defaultMessage="Risks Related to Ownership of our Interests" />
        </Typography>

        <ul>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-88"
                defaultMessage="There is currently no public trading market for our securities."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-89"
                defaultMessage="If a market ever develops for our Interests, the market price and trading volume may be volatile."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-90"
                defaultMessage="There may be state law restrictions on an investor’s ability to sell the Interests."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage id="disclaimer-paragraph-45" defaultMessage="Lack of voting rights." />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-91"
                defaultMessage='Each Offering is being conducted on a "best efforts" basis and we may not be able to execute our growth strategy if we are unable to raise this capital.'
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-92"
                defaultMessage="The offering price for the Interests determined by us may not necessarily bear any relationship to established valuation criteria such as earnings, book value or assets that may be agreed to between purchasers and sellers in private transactions or that may prevail in the market if and when our Interests can be traded publicly."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-93"
                defaultMessage="Funds from purchasers accompanying subscriptions for the Interests will not accrue interest while in escrow."
              />
            </Typography>
          </li>
          <li>
            <Typography variant={paragraphVariant}>
              <FormattedMessage
                id="disclaimer-paragraph-94"
                defaultMessage="Any dispute in relation to the operating agreement is subject to the exclusive jurisdiction of the Court of Chancery of the State of Delaware, except where federal law requires that certain claims be brought in federal courts.  The operating agreement, to the fullest extent permitted by applicable law, provides for investors to waive their right to a jury trial."
              />
            </Typography>
          </li>
        </ul>

        <Typography variant={titleVariant} marginTop={3}>
          <FormattedMessage id="disclaimer-title-5" defaultMessage="Third Party Information and Past Performance" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-95"
            defaultMessage="Certain information, including statistical data, third-party quotes and other factual statements, contained in the Communication has been obtained from published sources prepared by other parties considered to be generally reliable. However, none of the Company, the Manager or any affiliate of the Manager or any of their respective directors, shareholders, members, officers, employees or agents assumes any responsibility for the accuracy of such information. There is no representation or warranty, express or implied, as to the accuracy, adequateness or completeness of any such information used in the Communication."
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-96"
            defaultMessage="Past performance is not necessarily indicative of future results of the Interests or the assets in a given Series. Furthermore, to the extent the Communication relates to prior performance of assets similar to an Underlying Asset acquired or to be acquired by the Company, those similar assets may be materially different from, or may not be of the same quality as, the assets acquired or to be acquired by the Company. Values of comparable assets may vary depending on a number of factors, including market conditions, location of sale, associated taxes, originality of parts, condition of the asset, operating quality, historical significance, ownership history, level of wear and other factors."
          />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-97"
            defaultMessage="Furthermore, the value of Interests in a Series of the Company may materially differ from the value of the Underlying Asset for many reasons, including market factors, fees charged by the Asset Manager, and restrictions on liquidity."
          />
        </Typography>

        <Typography variant={titleVariant} marginTop={3}>
          <FormattedMessage id="disclaimer-title-6" defaultMessage="Forward-looking Statements" />
        </Typography>

        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-98"
            defaultMessage='The information contained in the Communication including the Company’s Offering Circulars may include some statements that are not historical and that are considered "forward-looking statements" within the meaning of Section 27A of the Securities Act. Such forward-looking statements may include, but are not limited to: statements regarding the development plans for the Company’s business; its strategies and business outlook; it’s market sector; anticipated development of the Company, the Manager and the Arrived&trade; Platform; and various other matters (including contingent liabilities and obligations and changes in accounting policies, standards and interpretations). These forward-looking statements typically express the Manager’s expectations, hopes, beliefs, and intentions regarding the future. In addition, without limiting the foregoing, any statements that refer to projections, forecasts or other characterizations of future events or circumstances, including any underlying assumptions, are forward-looking statements. The words "anticipates", "believes", "continue", "could", "estimates", "expects", "intends", "may", "might", "plans", "possible", "potential", "predicts", "projects", "seeks", "should", "will", "would" and similar expressions and variations, or comparable terminology, or the negatives of any of the foregoing, may identify forward-looking statements, but the absence of these words does not mean that a statement is not forward-looking.'
          />
        </Typography>
        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-99"
            defaultMessage='Any forward-looking statements contained in the Communication will be based on current expectations and beliefs concerning future developments that are difficult to predict. Neither the Company nor the Manager can guarantee future performance, or that future developments affecting the Company, the Manager or the Arrived&trade; Platform will be as currently anticipated. These forward-looking statements involve a number of risks, uncertainties (some of which are beyond our control) or other assumptions that may cause actual results or performance to be materially different from those expressed or implied by these forward-looking statements, including those risks set forth in "Risk Factors" above.'
          />
        </Typography>
        <Typography variant={paragraphVariant}>
          <FormattedMessage
            id="disclaimer-paragraph-100"
            defaultMessage="All forward-looking statements attributable to the Company are expressly qualified in their entirety by these risks and uncertainties. Should one or more of these risks or uncertainties materialize, or should any of the parties’ assumptions prove incorrect, actual results may vary in material respects from those projected in these forward-looking statements. The recipient of the Communication should not place undue reliance on any forward-looking statements and should not make an investment decision based solely on forward-looking statements. The Company undertakes no obligation to update or revise any forward-looking statements, whether as a result of new information, future events or otherwise, except as may be required under applicable securities laws."
          />
        </Typography>

        <Typography variant={titleVariant} marginTop={3}>
          <FormattedMessage id="disclaimer-title-7" defaultMessage="Obtain Your Own Advice" />
        </Typography>

        <Typography variant={paragraphVariant} marginBottom={3}>
          <FormattedMessage
            id="disclaimer-paragraph-101"
            defaultMessage="Prospective investors are not to construe the contents of the Communication as legal, business or tax advice. Each prospective investor should consult its own advisors as to legal, business, tax and related matters concerning the subject matter of the Communication and any applicable Offering."
          />
        </Typography>
      </PrivacyTermsWrapper>
      <PublicFooter />
    </>
  );
};
